// ContactCard.jsx
import React from 'react';
import { Card, Button } from 'react-bootstrap';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { BASE_URL } from '../../../../Constants/constants';
import {useTranslation} from 'react-i18next'

/**
 * ContactCard displays contact information in a read-only format.
 * It shows all contact fields in a grid layout and provides edit/delete actions.
 *
 * @param {Object} props
 * @param {Object} props.contactData - The contact information to display
 * @param {Object} props.currentUser - The current user's information
 * @param {Function} props.onEdit - Callback when edit button is clicked
 * @param {Function} props.onDelete - Callback when delete button is clicked
 */
const ContactCard = ({ contactData, currentUser, onEdit, onDelete }) => {
    const {t} = useTranslation('contact')

    // Define all possible fields to display
    const contactFields = [
        {
            label: t('callSign'),
            value: contactData.call_sign,
            // required: true
        },
        {
            label: t('officePhone'),
            value: contactData.office_phone
        },
        {
            label: t('dectPhone'),
            value: contactData.dect_phone
        },
        {
            label: t('mobilePhone'),
            value: contactData.mobile_phone
        },
        {
            label: t('satelliteNumber'),
            value: contactData.sat_number
        },
        {
            label: t('primaryEmergencyContactName'),
            value: contactData.primary_emergency_contact_name,
            required: true
        },
        {
            label: t('primaryEmergencyContactNumber'),
            value: contactData.primary_emergency_contact_number,
            required: true
        },
        {
            label: t('secondaryEmergencyContactName'),
            value: contactData.secondary_emergency_contact_name
        },
        {
            label: t('secondaryEmergencyContactNumber'),
            value: contactData.secondary_emergency_contact_number
        },
        {
            label: t('proofOfLife'),
            value: contactData.proof_of_life,
            isFile: true
        }
    ];

    /**
     * Renders the value for a field, handling special cases like files
     * @param {Object} field - The field configuration object
     * @returns {React.ReactNode} The rendered value
     */
    const renderFieldValue = (field) => {
        if (field.isFile && field.value) {
            return (
                <a
                    href={`${BASE_URL}/${field.value.replace('./proof/', 'proof/')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary"
                >
                    {t('fileDownload')}
                </a>
            );
        }

        return field.value || (
            <span className="text-muted fst-italic">
                {field.required ? t('notProvidedRequired') : t('notProvidedOptional')}
            </span>
        );
    };

    return (
        <Card className="shadow-lg p-4 mb-4 bg-light rounded">
            {/* Contact Information Grid */}
            <div className="row">
                {contactFields.map((field, index) => (
                    <div key={index} className="col-md-6 mb-3">
                        <div
                            className="p-3 rounded d-flex justify-content-between align-items-center"
                            style={{
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #dee2e6',
                                minHeight: '60px' // Ensures consistent height
                            }}
                        >
                            <strong className="me-2">
                                {field.label}
                                {field.required &&
                                    <span className="text-danger ms-1">*</span>
                                }:
                            </strong>
                            <span className="text-secondary text-end">
                                {renderFieldValue(field)}
                            </span>
                        </div>
                    </div>
                ))}
            </div>

            {/* Action Buttons */}
            <div className="d-flex justify-content-end mt-4">
                <Button
                    variant="outline-warning"
                    onClick={onEdit}
                    className="me-2 shadow-sm"
                >
                    <FaEdit className="me-1" /> {t('edit')}
                </Button>
                <Button
                    variant="outline-danger"
                    onClick={onDelete}
                    className="shadow-sm"
                >
                    <FaTrash className="me-1" /> {t('delete')}
                </Button>
            </div>
        </Card>
    );
};

export default ContactCard;